import { useId } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { CHART, monotonePath } from '@shared/ui/chart';
import { nivoTheme } from '@shared/ui/nivoTheme';

// Nivo, shared-theme versions of the three hand-rolled creator-card charts (trend,
// post/story frequency, per-post scatter). Lazy-loaded so Nivo ships on demand.

const compact = (v: number | null | undefined): string => {
  if (v == null) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(Math.round(v));
};
const tipBox: React.CSSProperties = {
  background: '#161a24', color: '#dde', fontSize: 12, borderRadius: 8, whiteSpace: 'nowrap',
  border: '1px solid rgba(74,158,255,0.35)', padding: '7px 10px', boxShadow: '0 8px 28px rgba(0,0,0,0.4)',
};
const dot = (c: string) => ({ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: c, marginRight: 6 } as React.CSSProperties);
const fmtDay = (d: string, locale: string) =>
  new Date((d.length <= 10 ? d + 'T00:00:00' : d)).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

function Legend({ items }: { items: [string, string, boolean?][] }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginTop: 6, paddingLeft: 4, flexWrap: 'wrap' }}>
      {items.map(([c, l, line], i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-caption)', color: '#8a94a6' }}>
          <span style={{ width: line ? 14 : 9, height: line ? 2 : 9, borderRadius: line ? 1 : 2, background: c, display: 'inline-block' }} />{l}
        </span>
      ))}
    </div>
  );
}

// ── Audience: followers + ER as two single-axis sparkline stat cards, each with its
//    latest value + Δ over the window. Replaces the misleading dual-axis trend line. ──
export function CreatorAudienceSparklines({ points, labels, locale = 'ru' }: {
  points: { date: string; followers: number | null; er: number | null }[];
  labels: { followers: string; er: string; deltaPct: string; deltaPp: string };
  locale?: string;
}) {
  type Pt = { date: string; v: number };
  const series = (pick: (p: typeof points[number]) => number | null): Pt[] =>
    points.filter((p) => pick(p) != null && p.date).map((p) => ({ date: p.date, v: pick(p) as number }));
  const fol = series((p) => p.followers);
  const ers = series((p) => p.er);
  const last = (a: Pt[]) => (a.length ? a[a.length - 1].v : null);
  const first = (a: Pt[]) => (a.length ? a[0].v : null);
  const fFirst = first(fol), fLast = last(fol);
  const eFirst = first(ers), eLast = last(ers);
  const fDelta = (fFirst != null && fLast != null && fFirst > 0) ? ((fLast - fFirst) / fFirst) * 100 : null;
  const eDelta = (eFirst != null && eLast != null) ? (eLast - eFirst) : null;

  /**
   * Snapshots are irregular — a weekly cron plus ad-hoc scrapes — so plotting them
   * on a point scale by array index drew six weeks and two days with the same
   * horizontal step. The x axis is real time now, and both ends of the y domain
   * are labelled: Nivo's `min/max: 'auto'` fits the domain to the data, which made
   * a 2.7% follower drift fill the whole card and read like a rocket. The range is
   * still data-fitted (a sparkline has to show shape), but you can now see what it is.
   */
  const spark = (pts: Pt[], color: string, fmtVal: (v: number) => string) => {
    const vals = pts.map((p) => p.v);
    const lo = Math.min(...vals), hi = Math.max(...vals);
    const pad = (hi - lo) === 0 ? Math.max(Math.abs(hi) * 0.02, 1) : (hi - lo) * 0.12;
    return (
      <div style={{ height: 74 }}>
        <ResponsiveLine
          data={[{ id: 's', data: pts.map((p) => ({ x: p.date, y: p.v })) }]}
          curve="monotoneX" colors={[color]}
          xScale={{ type: 'time', format: '%Y-%m-%d', precision: 'day', useUTC: false }}
          xFormat="time:%d.%m.%Y"
          yScale={{ type: 'linear', min: lo - pad, max: hi + pad }}
          enableArea areaOpacity={0.14} lineWidth={2} enablePoints={false}
          enableGridX={false} enableGridY={false}
          axisBottom={{ format: '%d.%m', tickValues: 3, tickSize: 0, tickPadding: 6 }}
          axisLeft={{ tickValues: [lo, hi], tickSize: 0, tickPadding: 5, format: (v) => fmtVal(Number(v)) }}
          margin={{ top: 8, right: 8, bottom: 20, left: 42 }}
          useMesh isInteractive
          tooltip={({ point }) => (
            <div style={tipBox}>
              <span style={dot(color)} />
              {/* Nivo hands back a Date on a time scale, the raw string otherwise. */}
              {fmtDay(
                typeof point.data.x === 'object' && point.data.x !== null && 'toISOString' in point.data.x
                  ? (point.data.x as Date).toISOString().slice(0, 10)
                  : String(point.data.x),
                locale,
              )}
              {' · '}<b>{fmtVal(Number(point.data.y))}</b>
            </div>
          )}
          theme={nivoTheme} animate={false}
        />
      </div>
    );
  };

  const card = (lbl: string, val: string, delta: number | null, unit: string, pts: Pt[], color: string, fmtVal: (v: number) => string) => {
    const up = (delta ?? 0) >= 0;
    const span = pts.length >= 2 ? `${fmtDay(pts[0].date, locale)} — ${fmtDay(pts[pts.length - 1].date, locale)}` : null;
    return (
      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.07)', padding: '10px 12px' }}>
        <div style={{ fontSize: 'var(--fs-caption)', color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4 }}>{lbl}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 2px' }}>
          <span style={{ fontSize: 20, fontWeight: 600, color: '#fff' }}>{val}</span>
          {delta != null && <span style={{ fontSize: 12, fontWeight: 600, color: up ? '#5fd17a' : '#f0806a' }}>{up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}{unit}</span>}
          {span && <span style={{ fontSize: 'var(--fs-micro)', color: '#667', marginLeft: 'auto' }}>{span}</span>}
        </div>
        {pts.length >= 2 ? spark(pts, color, fmtVal) : <div style={{ height: 74 }} />}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {card(labels.followers, compact(fLast), fDelta, labels.deltaPct, fol, CHART.palette[0], (v) => compact(v))}
      {card(labels.er, eLast != null ? `${eLast.toFixed(1)}%` : '—', eDelta, labels.deltaPp, ers, CHART.palette[1], (v) => `${v.toFixed(1)}%`)}
    </div>
  );
}

// ── Chart A/C: stacked bars (organic + branded) + avg-likes line via a custom layer ──
export function CreatorFrequencyNivo({ buckets, barColor = CHART.palette[0], height = 120, labels, onPick, selectedDates }: {
  buckets: { label: string; date?: string; organic: number; branded: number; avgLikes: number | null }[];
  barColor?: string;
  height?: number;
  labels: { organic: string; brand: string; avgLikes: string; posts: string };
  onPick?: (date: string) => void;
  selectedDates?: Set<string>;
}) {
  const hasBranded = buckets.some((b) => b.branded > 0);
  const maxLikes = Math.max(...buckets.map((b) => b.avgLikes ?? 0), 0);
  const hasLikes = maxLikes > 0;
  const anySel = !!selectedDates && selectedDates.size > 0;
  const data = buckets.map((b, i) => ({ idx: String(i), label: b.label, date: b.date ?? '', organic: b.organic, branded: b.branded, avgLikes: b.avgLikes }));
  const keys = hasBranded ? ['organic', 'branded'] : ['organic'];
  const step = Math.max(1, Math.ceil(buckets.length / 6));
  // Unique gradient ids per instance — two frequency charts render on the same card,
  // so a shared 'gbar' id would make the 2nd chart reference the 1st's gradient.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const gBar = `gbar${uid}`, gBrand = `gbrand${uid}`;
  const grad = (id: string, c: string) => ({ id, type: 'linearGradient', x1: 0, y1: 0, x2: 0, y2: 1, colors: [{ offset: 0, color: c, opacity: 0.95 }, { offset: 100, color: c, opacity: 0.4 }] });

  // Dim non-selected days as a non-interactive overlay ON TOP of the (default,
  // tooltip-wired) bars — a custom barComponent would swallow Nivo's hover tooltip.
  const DimLayer = (lp: { bars: readonly { x: number; y: number; width: number; height: number; data: { data: Record<string, unknown> } }[] }) => {
    if (!anySel) return null;
    return (
      <g pointerEvents="none">
        {lp.bars.filter((b) => !selectedDates!.has(String((b.data.data as { date?: string }).date || ''))).map((b, i) => (
          <rect key={i} x={b.x} y={b.y} width={b.width} height={b.height} rx={2} fill="#10141c" fillOpacity={0.62} />
        ))}
      </g>
    );
  };

  // custom Nivo-bar layer: avg-likes line through the bar centres, own y-scale
  const LikesLine = (lp: { bars: readonly { x: number; width: number; data: { id: string | number; data: Record<string, unknown> } }[]; innerHeight: number }) => {
    if (!hasLikes) return null;
    const pts = lp.bars
      .filter((bar) => String(bar.data.id) === 'organic')
      .map((bar) => ({ x: bar.x + bar.width / 2, av: (bar.data.data as { avgLikes: number | null }).avgLikes }))
      .filter((p) => p.av != null)
      .sort((a, b) => a.x - b.x)
      .map((p) => [p.x, lp.innerHeight * (1 - (p.av as number) / maxLikes)] as [number, number]);
    if (pts.length < 1) return null;
    return (
      <g pointerEvents="none">
        {pts.length >= 2 && <path d={monotonePath(pts)} fill="none" stroke={CHART.palette[1]} strokeWidth={2} />}
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={3} fill={CHART.palette[1]} />)}
      </g>
    );
  };

  return (
    <div>
    <div style={{ height }}>
      <ResponsiveBar
        data={data as unknown as Record<string, string | number>[]}
        keys={keys}
        indexBy="idx"
        colors={hasBranded ? [barColor, '#f5b700'] : [barColor]}
        borderRadius={2}
        padding={0.3}
        margin={{ top: 10, right: 8, bottom: 22, left: 30 }}
        axisLeft={{ tickSize: 0, tickPadding: 5, tickValues: 3, format: (v) => compact(v as number) }}
        enableGridX={false}
        enableGridY
        gridYValues={3}
        axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: data.filter((_, i) => i % step === 0).map((d) => d.idx), format: (idx) => data[Number(idx)]?.label ?? '' }}
        enableLabel={false}
        defs={[grad(gBar, barColor), grad(gBrand, '#f5b700')]}
        fill={hasBranded ? [{ match: { id: 'organic' }, id: gBar }, { match: { id: 'branded' }, id: gBrand }] : [{ match: '*', id: gBar }]}
        onClick={(bar: any) => { const dt = String(bar.data?.date || ''); if (onPick && dt) onPick(dt); }}
        layers={['grid', 'axes', 'bars', DimLayer, LikesLine, 'markers']}
        theme={nivoTheme}
        animate={false}
        tooltip={({ data: d }) => {
          const r = d as unknown as { label: string; organic: number; branded: number; avgLikes: number | null };
          const total = (r.organic || 0) + (r.branded || 0);
          return (
            <div style={tipBox}>
              <div style={{ fontWeight: 500, marginBottom: 3 }}>{r.label}</div>
              <div style={{ color: '#9aa' }}>{total} {labels.posts}{(r.branded || 0) > 0 ? ` · ${r.branded} ${labels.brand}` : ''}</div>
              {r.avgLikes ? <div style={{ color: '#9aa' }}>{labels.avgLikes}: <b style={{ color: '#dde' }}>{compact(r.avgLikes)}</b></div> : null}
            </div>
          );
        }}
      />
    </div>
      <Legend items={[
        [barColor, labels.organic],
        ...(hasBranded ? [['#f5b700', labels.brand] as [string, string]] : []),
        ...(hasLikes ? [[CHART.palette[1], labels.avgLikes, true] as [string, string, boolean]] : []),
      ]} />
    </div>
  );
}

// ── Chart B: per-post scatter, series by post type. Y is absolute engagement
//    (likes+comments) on a log scale — it spreads the dots across decades so hits
//    and duds read apart (ER% barely varies per creator and clumps them). ER% and
//    views live in the tooltip instead. Posts with a detected brand mention get a
//    gold ring; click a dot to filter the grid. Windowed to the SAME span as the
//    story-activity chart so the two read as a pair. ────────────────
const GOLD = '#f5b700';
const fmtPct = (v: number) => v >= 10 ? `${Math.round(v)}%` : v >= 1 ? `${v.toFixed(1)}%` : `${v.toFixed(2)}%`;
export function CreatorScatterNivo({ posts, followers, windowDays, locale, labels, onPick, selectedIds, brandedIds }: {
  posts: { id: string; post_type: string | null; posted_at: string | null; likes: number | null; comments: number | null; views?: number | null; is_paid_partnership?: boolean | null }[];
  followers: number | null;
  windowDays: number;
  locale: string;
  labels: { reelVideo: string; carousel: string; photo: string; likes: string; er: string; views: string; sponsor: string; clickHint: string };
  onPick?: (id: string) => void;
  selectedIds?: Set<string>;
  brandedIds?: Set<string>;
}) {
  const now = Date.now();
  const start = now - windowDays * 864e5;
  const foll = followers && followers > 0 ? followers : null;
  const typeOf = (tp: string | null) => (tp === 'reel' || tp === 'video') ? labels.reelVideo : (tp === 'carousel' || tp === 'sidecar') ? labels.carousel : labels.photo;
  const colorOf: Record<string, string> = { [labels.reelVideo]: CHART.palette[0], [labels.carousel]: CHART.palette[3], [labels.photo]: CHART.palette[5] };
  const groups = new Map<string, { x: number; y: number; id: string; likes: number | null; comments: number | null; views: number | null; er: number | null; branded: boolean }[]>();
  for (const p of posts) {
    if (!p.posted_at) continue;
    const ts = new Date(p.posted_at).getTime();
    if (ts < start) continue;
    const eng = (p.likes ?? 0) + (p.comments ?? 0);
    if (eng <= 0) continue; // no engagement signal → nothing to place
    const er = foll != null ? (eng / foll) * 100 : null;
    const g = typeOf(p.post_type);
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push({ x: ts, y: Math.max(1, eng), id: p.id, likes: p.likes, comments: p.comments, views: p.views ?? null, er, branded: brandedIds?.has(p.id) ?? false });
  }
  const series = [...groups.entries()].map(([id, data]) => ({ id, data }));
  const ys = series.flatMap((s) => s.data.map((d) => d.y));
  const maxY = Math.max(10, ...ys);
  const yTop = Math.pow(10, Math.ceil(Math.log10(maxY)));
  const present = new Set(series.map((s) => s.id));
  const legendItems = ([[CHART.palette[0], labels.reelVideo], [CHART.palette[3], labels.carousel], [CHART.palette[5], labels.photo]] as [string, string][])
    .filter(([, name]) => present.has(name));
  const fmtY = (v: number) => compact(v);
  return (
    <div>
    <div style={{ height: 120 }}>
      <ResponsiveScatterPlot
        data={series}
        colors={(s: { serieId: string | number }) => colorOf[String(s.serieId)] ?? CHART.other}
        xScale={{ type: 'linear', min: start, max: now }}
        yScale={{ type: 'log', base: 10, min: 1, max: yTop }}
        nodeSize={9}
        margin={{ top: 10, right: 12, bottom: 22, left: 42 }}
        enableGridX={false}
        gridYValues={3}
        axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 3, format: (v) => fmtY(v as number) }}
        axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: 4, format: (v) => fmtDay(new Date(v as number).toISOString(), locale) }}
        onClick={(node: any) => onPick?.(node.data.id)}
        nodeComponent={(p: any) => {
          const n = p.node, d = n.data;
          const anySel = !!selectedIds && selectedIds.size > 0;
          const isSel = anySel && selectedIds!.has(d.id);
          const r = (isSel ? 7 : 4.5);
          return (
            <circle
              cx={n.x} cy={n.y} r={r} fill={n.color}
              fillOpacity={anySel && !isSel ? 0.25 : 0.85}
              stroke={d.branded ? GOLD : (isSel ? '#fff' : 'none')}
              strokeWidth={d.branded ? 2.5 : (isSel ? 1.5 : 0)}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => p.onMouseEnter?.(n, e)}
              onMouseMove={(e) => p.onMouseMove?.(n, e)}
              onMouseLeave={(e) => p.onMouseLeave?.(n, e)}
              onClick={(e) => p.onClick?.(n, e)}
            />
          );
        }}
        theme={nivoTheme}
        animate={false}
        tooltip={({ node }: any) => (
          <div style={tipBox}>
            <div style={{ fontWeight: 500, marginBottom: 3 }}><span style={dot(node.serieColor)} />{node.serieId}{node.data.branded ? <span style={{ color: GOLD, marginLeft: 6 }}>● {labels.sponsor}</span> : null}</div>
            <div style={{ color: '#8a94a6', fontSize: 11 }}>{fmtDay(new Date(node.data.x).toISOString(), locale)}</div>
            {node.data.er != null && <div style={{ color: '#dde' }}>{fmtPct(node.data.er)} {labels.er}</div>}
            <div style={{ color: '#9aa' }}>{compact(node.data.likes)} {labels.likes}{node.data.comments ? ` · ${compact(node.data.comments)}` : ''}{node.data.views ? ` · ${compact(node.data.views)} ${labels.views}` : ''}</div>
          </div>
        )}
      />
    </div>
      <Legend items={legendItems} />
      <div style={{ fontSize: 'var(--fs-caption)', color: '#8a94a6', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', border: `2px solid ${GOLD}`, display: 'inline-block' }} />{labels.sponsor} · <span style={{ opacity: 0.8 }}>{labels.clickHint}</span>
      </div>
    </div>
  );
}
